<template>
  <v-card class="madeouts-box" flat>
    <header class="madeouts-header">
      <div class="madeouts-header-main">
        <div class="madeouts-eyebrow">Palojen tekijät</div>
        <h3 class="madeouts-title">Eniten paloja tehneet</h3>
        <div class="madeouts-subtitle">
          Top 5 valitulta kaudelta
        </div>
      </div>
    </header>

    <div class="madeouts-body">
      <div v-if="!canFetch" class="madeouts-empty">
        Valitse joukkue ja kausi.
      </div>

      <div v-else-if="loading" class="madeouts-loading">
        <div v-for="i in 5" :key="i" class="skeleton-row" />
      </div>

      <v-alert v-else-if="error" type="error" dense outlined class="madeouts-error">
        {{ error }}
      </v-alert>

      <div v-else-if="!items.length" class="madeouts-empty">
        Ei tehtyjä paloja valitulta kaudelta.
      </div>

      <table v-else class="madeouts-table">
        <thead>
          <tr>
            <th class="col-rank">#</th>
            <th class="col-player">Pelaaja</th>
            <th class="col-made-outs">Palot</th>
            <th class="col-games">Ottelut</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="`madeout-${item.player_id || item.player_name}-${index}`"
          >
            <td class="col-rank">{{ index + 1 }}</td>
            <td class="col-player">
              <div class="player-cell">
                <div class="player-avatar">
                  <img
                    v-if="item.picture_url"
                    :src="item.picture_url"
                    :alt="item.player_name"
                  />
                  <span v-else class="player-initials">
                    {{ initials(item.player_name) }}
                  </span>
                </div>
                <div class="player-name">{{ item.player_name || '–' }}</div>
              </div>
            </td>
            <td class="col-made-outs">{{ item.made_outs }}</td>
            <td class="col-games">{{ item.games_played }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import ax from '@/utils/axios/axios'

export default {
  name: 'TopMadeOutsBox',
  props: {
    leagueId: {
      type: Number,
      default: null
    },
    leagueClubId: {
      type: Number,
      default: null
    },
    seasonId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    items: [],
    loading: false,
    error: null,
    requestId: 0
  }),
  computed: {
    ...mapGetters('admin', [
      'season_by_id'
    ]),
    season() {
      return this.seasonId ? this.season_by_id(this.seasonId) : null
    },
    seasonRange() {
      if(!this.season || !this.season.starttime || !this.season.endtime) return null

      const start = new Date(this.season.starttime).getTime()
      const end = new Date(this.season.endtime).getTime()
      if(Number.isNaN(start) || Number.isNaN(end)) return null

      return { start, end }
    },
    canFetch() {
      return Boolean(
        this.leagueId &&
        this.leagueClubId &&
        this.seasonId &&
        this.seasonRange
      )
    },
    requestKey() {
      return [
        this.leagueId,
        this.leagueClubId,
        this.seasonId
      ].join(':')
    }
  },
  watch: {
    requestKey: {
      immediate: true,
      handler() {
        this.refresh()
      }
    }
  },
  methods: {
    refresh() {
      if(!this.canFetch) {
        this.items = []
        this.error = null
        this.loading = false
        return
      }

      const requestId = this.requestId + 1
      this.requestId = requestId
      this.loading = true
      this.error = null

      const { start, end } = this.seasonRange
      ax.get('/event/made-outs', {
        params: {
          start,
          end,
          limit: 5,
          offset: 0,
          league_id: this.leagueId,
          league_club_id: this.leagueClubId
        }
      })
        .then(res => {
          if(requestId !== this.requestId) return
          this.items = Array.isArray(res.data?.items) ? res.data.items : []
        })
        .catch(err => {
          if(requestId !== this.requestId) return
          this.items = []
          this.error = err.response?.data?.error || 'Tehtyjen palojen haku epäonnistui'
        })
        .finally(() => {
          if(requestId === this.requestId) this.loading = false
        })
    },
    initials(name) {
      if(!name) return ''
      return name
        .split(/\s+/)
        .filter(Boolean)
        .map(part => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    }
  }
}
</script>

<style scoped>
.madeouts-box {
  margin-top: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.madeouts-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.madeouts-header-main {
  min-width: 0;
}

.madeouts-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
}

.madeouts-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.madeouts-subtitle {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}

.madeouts-body {
  padding: 4px 0;
}

.madeouts-empty {
  padding: 32px 24px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}

.madeouts-loading {
  padding: 12px 24px 16px;
}

.skeleton-row {
  height: 44px;
  margin: 6px 0;
  border-radius: 6px;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.madeouts-error {
  margin: 16px 24px;
}

.madeouts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.madeouts-table thead th {
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  text-align: left;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.madeouts-table thead th.col-rank,
.madeouts-table thead th.col-made-outs,
.madeouts-table thead th.col-games {
  text-align: center;
}

.madeouts-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 120ms ease;
}

.madeouts-table tbody tr:last-child {
  border-bottom: none;
}

.madeouts-table tbody tr:hover {
  background: #fafafa;
}

.madeouts-table td {
  padding: 12px 16px;
  vertical-align: middle;
  color: #111827;
}

.col-rank {
  width: 48px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #6b7280;
  text-align: center;
}

.col-made-outs,
.col-games {
  width: 100px;
  font-variant-numeric: tabular-nums;
  text-align: center;
  font-weight: 500;
}

.col-made-outs {
  font-weight: 700;
}

.col-player {
  min-width: 0;
}

.player-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.player-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.player-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.player-initials {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.player-name {
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
